import { FocusMonitor } from "@angular/cdk/a11y"
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  Optional,
  Self,
  signal,
  untracked,
  viewChild
} from "@angular/core"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  Validators
} from "@angular/forms"
import {
  MAT_FORM_FIELD,
  MatFormFieldControl,
  MatFormFieldModule
} from "@angular/material/form-field"
import { Subject } from "rxjs"

/** Data structure for holding telephone number. */
export class MyTel {
  constructor(
    public area: string,
    public exchange: string,
    public subscriber: string
  ) {}
}

/** Custom `MatFormFieldControl` for telephone number input. */
@Component({
  selector: "example-tel-input",
  template: `
    <div
      role="group"
      class="example-tel-input-container"
      [formGroup]="parts"
      [attr.aria-labelledby]="_formField?.getLabelId()"
      (focusin)="onFocusIn()"
      (focusout)="onFocusOut($event)">
      <input
        class="example-tel-input-element"
        formControlName="area"
        size="3"
        maxLength="3"
        aria-label="Area code"
        (input)="_handleInput(parts.controls.area, exchange)"
        #area />
      <span class="example-tel-input-spacer">&ndash;</span>
      <input
        class="example-tel-input-element"
        formControlName="exchange"
        maxLength="3"
        size="3"
        aria-label="Exchange code"
        (input)="_handleInput(parts.controls.exchange, subscriber)"
        (keyup.backspace)="autoFocusPrev(parts.controls.exchange, area)"
        #exchange />
      <span class="example-tel-input-spacer">&ndash;</span>
      <input
        class="example-tel-input-element"
        formControlName="subscriber"
        maxLength="4"
        size="4"
        aria-label="Subscriber number"
        (input)="_handleInput(parts.controls.subscriber)"
        (keyup.backspace)="autoFocusPrev(parts.controls.subscriber, exchange)"
        #subscriber />
    </div>
  `,
  providers: [{ provide: MatFormFieldControl, useExisting: MyTelInput }],
  host: {
    "[class.example-floating]": "shouldLabelFloat",
    "[id]": "id"
  },
  imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyTelInput
  implements ControlValueAccessor, MatFormFieldControl<MyTel>, OnDestroy
{
  static nextId = 0
  readonly areaInput = viewChild.required<HTMLInputElement>("area")
  readonly exchangeInput = viewChild.required<HTMLInputElement>("exchange")
  readonly subscriberInput = viewChild.required<HTMLInputElement>("subscriber")

  readonly parts: FormGroup<{
    area: FormControl<string | null>
    exchange: FormControl<string | null>
    subscriber: FormControl<string | null>
  }>
  readonly stateChanges = new Subject<void>()
  readonly touched = signal(false)
  readonly controlType = "example-tel-input"
  readonly id = `example-tel-input-${MyTelInput.nextId++}`
  readonly _userAriaDescribedBy = input<string>("", {
    alias: "aria-describedby"
  })
  readonly _placeholder = input<string>("", { alias: "placeholder" })
  readonly _required = input<boolean, unknown>(false, {
    alias: "required",
    transform: booleanAttribute
  })
  readonly _disabledByInput = input<boolean, unknown>(false, {
    alias: "disabled",
    transform: booleanAttribute
  })
  readonly _value = model<MyTel | null>(null, { alias: "value" })
  onChange: (_: unknown) => void = () => {}
  onTouched = () => {}

  protected readonly _formField = inject(MAT_FORM_FIELD, {
    optional: true
  })

  private readonly _focused = signal(false)
  private readonly _disabledByCva = signal(false)
  private readonly _disabled = computed(
    () => this._disabledByInput() || this._disabledByCva()
  )
  private readonly _focusMonitor = inject(FocusMonitor)
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef)

  get focused(): boolean {
    return this._focused()
  }

  get empty() {
    const {
      value: { area, exchange, subscriber }
    } = this.parts

    return !area && !exchange && !subscriber
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty
  }

  get userAriaDescribedBy() {
    return this._userAriaDescribedBy()
  }

  get placeholder(): string {
    return this._placeholder()
  }

  get required(): boolean {
    return this._required()
  }

  get disabled(): boolean {
    return this._disabled()
  }

  get value(): MyTel | null {
    return this._value()
  }

  get errorState(): boolean {
    return this.parts.invalid && this.touched()
  }

  constructor(@Optional() @Self() public ngControl: NgControl) {
    // Setting the value accessor directly (instead of using
    // the providers) to avoid running into a circular import.
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this
    }

    this.parts = inject(FormBuilder).group({
      area: [
        "",
        [Validators.required, Validators.minLength(3), Validators.maxLength(3)]
      ],
      exchange: [
        "",
        [Validators.required, Validators.minLength(3), Validators.maxLength(3)]
      ],
      subscriber: [
        "",
        [Validators.required, Validators.minLength(4), Validators.maxLength(4)]
      ]
    })

    effect(() => {
      // Read signals to trigger effect.
      this._placeholder()
      this._required()
      this._disabled()
      // Propagate state changes.
      untracked(() => this.stateChanges.next())
    })

    effect(() => {
      if (this._disabled()) {
        untracked(() => this.parts.disable())
      } else {
        untracked(() => this.parts.enable())
      }
    })

    effect(() => {
      const value = this._value() || new MyTel("", "", "")
      untracked(() => this.parts.setValue(value))
    })

    this.parts.statusChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      console.log("statusChanges")
      this.stateChanges.next()
    })

    this.parts.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      console.log("valueChanges")
      const tel = this.parts.valid
        ? new MyTel(
            value.area || "",
            value.exchange || "",
            value.subscriber || ""
          )
        : null
      this._updateValue(tel)
    })
  }

  ngOnDestroy() {
    this.stateChanges.complete()
    this._focusMonitor.stopMonitoring(this._elementRef)
  }

  onFocusIn() {
    if (!this._focused()) {
      this._focused.set(true)
    }
  }

  onFocusOut(event: FocusEvent) {
    if (
      !this._elementRef.nativeElement.contains(event.relatedTarget as Element)
    ) {
      this.touched.set(true)
      this._focused.set(false)
      this.onTouched()
    }
  }

  autoFocusNext(
    control: AbstractControl,
    nextElement?: HTMLInputElement
  ): void {
    if (!control.errors && nextElement) {
      this._focusMonitor.focusVia(nextElement, "program")
    }
  }

  autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
    if (control.value.length < 1) {
      this._focusMonitor.focusVia(prevElement, "program")
    }
  }

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement.querySelector(
      ".example-tel-input-container"
    )!
    controlElement.setAttribute("aria-describedby", ids.join(" "))
  }

  onContainerClick() {
    if (this.parts.controls.subscriber.valid) {
      this._focusMonitor.focusVia(this.subscriberInput(), "program")
    } else if (this.parts.controls.exchange.valid) {
      this._focusMonitor.focusVia(this.subscriberInput(), "program")
    } else if (this.parts.controls.area.valid) {
      this._focusMonitor.focusVia(this.exchangeInput(), "program")
    } else {
      this._focusMonitor.focusVia(this.areaInput(), "program")
    }
  }

  writeValue(tel: MyTel | null): void {
    this._updateValue(tel)
  }

  registerOnChange(fn: (val: unknown) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled)
  }

  _handleInput(control: AbstractControl, nextElement?: HTMLInputElement): void {
    this.autoFocusNext(control, nextElement)
    this.onChange(this.value)
  }

  private _updateValue(tel: MyTel | null) {
    const current = this._value()
    console.log("_updateValue", { tel, current })
    if (
      tel === current ||
      (tel?.area === current?.area &&
        tel?.exchange === current?.exchange &&
        tel?.subscriber === current?.subscriber)
    ) {
      return
    }
    this._value.set(tel)
  }
}
